import router from "@/router";
import store from "@/store";
import axios from "axios";
import {
  ERR_ALREADY_EXISTS,
  ERR_BAD_REQUEST,
  ERR_MAX_QUERY,
  ERR_NOT_FOUND,
  ERR_TOO_MANY_REQUEST,
  ERR_UNAUTHORIZED,
  ERR_UPGRADE_REQUIRED,
  ERR_PERMISSION_ISSUE,
} from "./err";
import { handleBlobRes } from "./index";
import { getIsAccessDisabled } from "./permissions";
import Vue from "vue";

// Coalesce identical error toasts inside a 2s window. Stops bursts of API failures
// (e.g. three /inbox endpoints failing in parallel with "Missing parameter") from
// stacking three identical red toasts on top of each other.
const recentErrorToasts = new Map();
const ERROR_TOAST_DEDUP_MS = 2000;
export function showErrorToast(message, options) {
  const key = String(message || "");
  if (!key) return;
  const now = Date.now();
  const last = recentErrorToasts.get(key);
  if (last && now - last < ERROR_TOAST_DEDUP_MS) return;
  recentErrorToasts.set(key, now);
  if (recentErrorToasts.size > 50) {
    // Prevent unbounded growth — drop entries older than the dedup window.
    for (const [k, t] of recentErrorToasts) {
      if (now - t > ERROR_TOAST_DEDUP_MS) recentErrorToasts.delete(k);
    }
  }
  if (options) Vue.$toast.error(message, options); else Vue.$toast.error(message);
}

const request = axios.create({
  baseURL: process.env.VUE_APP_BASE_API,
  headers: {
    "Content-Type": "application/json",
  },
});

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    if (store.state.user.token) {
      config.headers["Authorization"] = `Bearer ${store.state.user.token}`;
    }
    // Forward the app-language to the backend so endpoints like /locations
    // can pick the right column (backend resolves `common_name${language_prefix}`
    // based on this header). Browser default Accept-Language reflects OS
    // settings, not our in-app picker — explicit override keeps them in sync.
    const lang_code = store.state.user.language && store.state.user.language.code;
    if (lang_code) {
      config.headers["Accept-Language"] = lang_code;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    const { status, data } = response;
    if (status === 200) {
      return data;
    } else {
      return Promise.reject(new Error(`HTTP Error: ${status}`));
    }
  },
  async (error) => {
    // Aborted requests (debounce-cancelled search, route-change cancellations) are
    // intentional, not failures — don't toast or log them as errors.
    if (axios.isCancel(error) || error.code === "ERR_CANCELED" || error.name === "CanceledError" || error.name === "AbortError") {
      return Promise.reject(Object.assign(error, { popuped: true }));
    }
    // Opt-in suppression — callers that treat the request as non-critical
    // (e.g. background catalog refreshes with a local fallback) can pass
    // `{ silent: true }` in axios config to skip toast + console noise.
    if (error.config?.silent) {
      return Promise.reject(Object.assign(error, { popuped: true }));
    }
    console.log(error);
    if (error.response) {
      if (error?.response?.data) {
        const { code, details } = error.response.data;
        if (details?.is_timeout_query_retry_available) {
          store.commit("common/add_sequence_credential_authentication_retry");
          return;
        }
      }
      const { default_notification_bar_detail_delay_second } =
        store.state.user.systemConfig || {};
      switch (error.response.status) {
        case ERR_UNAUTHORIZED:
          showErrorToast("Unauthorized, please login again");
          store.commit("user/removeToken");
          store.commit("user/removeApikey");
          setTimeout(() => {
            router.replace({
              path: "/login",
              query: {
                redirect: router.currentRoute.fullPath,
              },
            });
          }, 1500);
          break;
        case ERR_NOT_FOUND:
          // A coded 404 carries a real message; the generic toast is only for a true missing-route 404.
          showErrorToast(
            error?.response?.data?.code
              ? error?.response?.data?.details?.message ||
                  error?.response?.data?.message
              : "Network request does not exist",
          );
          break;
        case ERR_BAD_REQUEST:
        case ERR_ALREADY_EXISTS:
          showErrorToast(
            error?.response?.data?.details?.message ||
              error?.response?.data?.message,
          );
          throw Object.assign(error, { popuped: true });
        case 409: // 目前只有大类，报too many request
          showErrorToast(error.response?.data?.message);
          return;
        case 402:
          const errorCode = error.response.data.code;
          if (errorCode === 40201) {
            store.commit("common/setPayDialog", true);
          } else if (errorCode === 40208) {
            if (error.config?.handleError) {
              return Promise.reject(error);
            }
            store.commit("common/setPaymentSecurityDialog", true);
          } else if (
            errorCode &&
            typeof errorCode === "number" &&
            errorCode >= 40200 &&
            errorCode <= 40299
          ) {
            break;
          } else {
            // 不是 402xx 格式的错误码，尝试处理 blob 响应
            try {
              const { showError } = await handleBlobRes(
                error.response.data,
                40208,
              );
              if (showError) {
                store.commit("common/setPaymentSecurityDialog", true);
              }
            } catch (e) {
              console.error("Error handling blob response:", e);
            }
          }
          break;
        case 406:
          if (error.response.data.code === 40606) {
            // 返回类型不为blob
            store.commit("common/setNotEnoughCredits", true);
          } else if (
            errorCode &&
            typeof errorCode === "number" &&
            errorCode >= 40600 &&
            errorCode <= 40699
          ) {
            break;
          } else {
            // 返回类型为blob
            try {
              const { showError } = await handleBlobRes(
                error.response.data,
                40606,
              );
              if (showError) {
                store.commit("common/setNotEnoughCredits", true);
              } else {
                showErrorToast(error.response?.data?.message || "406 Error", {
                  timeout: default_notification_bar_detail_delay_second
                    ? default_notification_bar_detail_delay_second * 1000
                    : 5000,
                });
              }
            } catch (e) {
              console.log("Error handling blob response:", e);
              showErrorToast(error.response?.data?.message || "406 Error", {
                timeout: default_notification_bar_detail_delay_second
                  ? default_notification_bar_detail_delay_second * 1000
                  : 5000,
              });
            }
          }
          break;
        case 426:
          store.commit("common/setUpgradeDialog", true);
          break;
        case 429:
          if (error.response.data.code === 42903) {
            store.commit("people/setLimitPopup", true);
          } else {
            showErrorToast(error.response?.data?.message);
          }
          break;
        case ERR_MAX_QUERY:
          store.commit("common/setMaximumQueryLimitShow");
          break;
        case ERR_UPGRADE_REQUIRED:
          break;

        // 403
        case ERR_PERMISSION_ISSUE:
          const _code = error?.response?.data?.code;
          if (!getIsAccessDisabled(_code)) {
            showErrorToast(error.response?.data?.message);
          }
          break;
      }
    } else {
      if (error.code === "ERR_NETWORK") {
        showErrorToast("Network Error");
      }
      // return Promise.reject(error);
    }

    throw error;
  },
);

export default request;

/**
 * axios interceptors handler
 * 1. error?.code === 'ERR_NETWORK' => Network Error
 * 2. error?.response?.data?.details?.is_timeout_query_retry_available => sequence creditentail authentication handle
 *
 * error.response.status
 * 1. ERR_UNAUTHORIZED => toast pop up and redirect to login page
 * 2. ERR_NOT_FOUND => toast pop up
 * 3. ERR_BAD_REQUEST, ERR_ALREADY_EXISTS => toast pop up with error message from backend
 * 4. 409 => toast pop up with error message from backend
 * 5. ERR_PERMISSION_ISSUE => check if error?.response?.data?.code is 40348 or 40349, if not, toast pop up with error message from backend
 * 6. 402 => payment issue
 * 7. 406 => credit issue
 * 8. 426 => upgrade issue
 * 9. 429 => some limit xxxx
 * 10. ERR_MAX_QUERY => maximum query limit reached
 * 11. ERR_UPGRADE_REQUIRED => not sure
 */
export const handleRequestErrorPopup = (error, message = "") => {
  /**
   * 1. check if error is axios error;
   * 2. if axios => check if popuped by interceptors
   * 3. if not popuped => show toast with message
   */

  if (axios.isAxiosError(error)) {
    if (error?.code === "ERR_NETWORK") {
      return;
    }

    if (error?.response?.data?.details?.is_timeout_query_retry_available) {
      return;
    }

    switch (error?.response?.status) {
      case ERR_UNAUTHORIZED:
      case ERR_NOT_FOUND:
      case ERR_BAD_REQUEST:
      case ERR_ALREADY_EXISTS:
      case 409:
      case ERR_PERMISSION_ISSUE:
      case 402:
      case 406:
      case 426:
      case 429:
      case ERR_MAX_QUERY:
      case ERR_UPGRADE_REQUIRED:
        return;
      default:
        if (!error?.popuped && message) {
          showErrorToast(error?.response?.data?.message || message);
        }
        return;
    }
  } else {
    if (!error?.popuped && message) {
      showErrorToast(message);
    }
  }
};
